import SharedDialog from '../../../shared/shared-dialog';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Dispatch, SetStateAction, useState } from 'react';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useStore } from '../../state/zustand-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModuleList } from '../../../shared/analytics/module-list';
import { handleErrors } from '../../../utils/errors';
import { UserKeys, disableDevices } from '../../../api/users-api';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ChromeDeviceModel, Page } from '../../../../types';
import Typography from '@mui/material/Typography';

interface DisableDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDevices: ChromeDeviceModel[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function DisableDeviceDialog({
  open,
  handleClose,
  selectedDevices,
  setSelectedRows,
  changeLogRoles,
}: DisableDeviceDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const deviceIds = selectedDevices.map((device) => device.deviceId);

  const { mutate, isPending } = useMutation({
    mutationFn: () => disableDevices(deviceIds, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          deviceIds.length > 1
            ? 'Devices disabled successfully!'
            : 'Device disabled successfully!',
      });

      const chromeDeviceQueries = queryClient.getQueriesData<
        Page<ChromeDeviceModel[]>
      >({
        queryKey: [UserKeys.ChromeDevicesPage],
      });

      if (chromeDeviceQueries.length > 0) {
        chromeDeviceQueries.map((chromeDeviceQuery) => {
          const queryKey = chromeDeviceQuery[0];
          const deviceList = chromeDeviceQuery[1];
          if (
            deviceList?.results.some((device) =>
              deviceIds.includes(device.deviceId)
            )
          ) {
            queryClient.setQueryData<Page<ChromeDeviceModel[]>>(
              queryKey,
              (oldData) => {
                if (!oldData) return;

                const newData = {
                  ...oldData,
                  results: oldData.results.map((device) =>
                    deviceIds.includes(device.deviceId)
                      ? {
                          ...device,
                          status: 'DISABLED',
                        }
                      : device
                  ),
                };
                return newData;
              }
            );
          }
        });
      }

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          deviceIds.length > 1
            ? 'There was an issue disabling the devices. Please refresh and try again.'
            : 'There was an issue disabling the device. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.devices.device_management.disable_devices, {
        module: ModuleList.modules.devices.name,
        section: ModuleList.modules.devices.sections.chromeDevices,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CanDisableDevices.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={
          selectedDevices.length > 1 ? 'Disable Devices' : 'Disable Device'
        }
        dialogContent={
          <Typography>
            {selectedDevices.length > 1 ? (
              <>
                {'Disable '}
                <strong>{selectedDevices.length} selected devices</strong>
                {'?'}
              </>
            ) : (
              <>
                {'Disable device number '}
                <strong>{selectedDevices[0]?.serialNumber}</strong>
                {'?'}
              </>
            )}
          </Typography>
        }
        primaryButton={{
          title:
            selectedDevices.length > 1 ? 'Disable Devices' : 'Disable Device',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending,
          color: 'error',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
